import { Injectable } from '@angular/core';
import {
  RevDoc,
  RevRule,
  RevCatalog,
  RevDocsSearchParams,
  RevRulesSearchParams,
  RevStatus,
  CardMatch,
  ImageMatch,
  PageToolRevPhrase,
  PageToolRevImage,
} from '../models/rev.model';
import { Observable, of, Subject, throwError } from 'rxjs';
import { ServiceOptions } from '../models/service-options';
import { map, catchError, take, startWith } from 'rxjs/operators';
import { ServiceMetaData } from '../models/service-meta.model';
import { actionsList, AppState } from '../redux/store';
import { NgRedux } from '@redux/redux-compatibility.service';
import { HttpClient } from '@angular/common/http';
import { AuthService } from './auth.service';
import { SharedService } from './shared.service';
import { DatePipe } from '@angular/common';
import {
  DataElement,
  DocPage,
  ReferencePhrase,
  AddInstr,
  PhraseWord,
  CatMatch,
  MatchDiff,
  Product,
  CardEntryMatch,
  BoundingBoxMatch,
  RefMatch,
  WordChar,
  ServiceCallInfo,
} from '../models/common';
import { TranslateService } from '@ngx-translate/core';
import { CatService } from './cat.service';
import { BoundingBox, Card } from '../models/cat.model';
import { Config } from 'src/app/config';
import { RevRuleCacheObject } from '@shared/models/rev-rule-cache-object.model';
import { Params } from '@angular/router';
import { reference } from '@popperjs/core';
import { ClaimToolElement } from '@shared/models/claim-tool-element.model';
import { ClaimToolElementSaveChangesData } from '@shared/models/claim-tool-element-save-changes-data.model';

export interface RevClaimToolSetup {
  element: ClaimToolElement;
  saveChangesInitialValues: ClaimToolElementSaveChangesData;
}

export interface RevClaimBlockResult {
  claim_phrase_id: number;
  claim_text_phrase_ids: number[];
  reevaluation_initiated: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class RevService {
  mockupSw: boolean = true;
  private mockupDataUrl = 'assets/mock/';
  private scaiServiceUrl: string;
  public changePdfPage$ = new Subject();
  public pdfLoaded$ = new Subject();
  public showAnnotationForRule$ = new Subject();
  public hideAnnotationForRule$ = new Subject();
  public focusAnnotationForRule$ = new Subject<RevRule>();
  public propagateActionFromParentRule$  = new Subject();
  public revIgnoredStatusesLoaded$ = new Subject();
  pdfAnnotationRenderReady$ = new Subject();

  // check should trigger search in rev doc rules list in ngOnInit 
  public shouldSearch: boolean = false;
  
  constructor(
    private ngRedux: NgRedux<AppState>,
    private http: HttpClient,
    private authService: AuthService,
    private datePipe: DatePipe,
    private sharedService: SharedService,
    private translateService: TranslateService,
    private catService: CatService
  ) {
    this.scaiServiceUrl = Config.assetConfigData['scaiServiceUrl'];
  }

  createClaimToolSetup(rule: RevRule, doc: RevDoc): RevClaimToolSetup {
    const element = new ClaimToolElement();
    element.contentType = rule.revRuleContentType;
    element.contentTypeDesc = rule.revRuleContentTypeDesc;
    element.phraseWords = JSON.parse(JSON.stringify(rule.ruleDispWords));
    element.phraseId = rule.phraseId;
    element.isPrecheck = true;
    element.docId = doc.docId;
    element.runType = 'DOC_REV_REVALUATE';
    element.envId = this.ngRedux.getState().curEnv.id;
    element.catVersionId = doc.catVersionId;
    element.phraseSubTypeCode = rule.subTypeCode;
    element.phraseSubTypeCodeDescr = rule.subTypeDescr;
    element.phraseTypeCode = rule.typeCode;
    element.phraseTypeCodeDescr = rule.typeDescr;
    element.pageNum = rule.pageNum - 1;

    const saveChangesInitialValues = new ClaimToolElementSaveChangesData();
    saveChangesInitialValues.contentType = rule.revRuleContentType;
    saveChangesInitialValues.phraseTypeCode = rule.typeCode;
    saveChangesInitialValues.phraseSubTypeCode = rule.subTypeCode;
    return { element, saveChangesInitialValues };
  }

  getSingleRevDoc(docId: number, envId: number, includeInactiveSw: boolean = true): Observable<RevDoc> {
    
    let response = new Observable<RevDoc>();
    let serviceName = 'get_rev_docs_func';
    let params = new ServiceOptions();
    params.serviceName = serviceName;
    params.urldecodeSw = false;
    params['docId'] = docId;
    params['envId'] = envId;
    params['includeInactiveSw'] = includeInactiveSw;
    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json',
      })
      .pipe(
        map((data) => this._extractSingleRevDoc(data, info)),
        catchError((error) => {
          return this.authService.handleError(error, info);
        })
      );
      return response;
  }
  getRevDocs(
    searchParams: RevDocsSearchParams,
    dispatchDocList: boolean = true,
    newSearch: boolean = true
  ): Observable<RevDoc[]> {
    /*if (this.mockupSw) {
      return this.http
        .get(this.mockupDataUrl + 'get_rev_docs.json')
        .pipe(map((res) => this.extractGetRevDocs(res)));
    }*/

    let response = new Observable<RevDoc[]>();
    let serviceName = 'get_rev_docs_func';
    let params = new ServiceOptions();
    params.serviceName = serviceName;
    params.urldecodeSw = false;

    params['envId'] = this.ngRedux.getState().curEnv.id;
    if (searchParams.docId) params['docId'] = searchParams.docId;
    if (searchParams.docName) params['docName'] = searchParams.docName;
    if (searchParams.docIdName) params['docIdName'] = searchParams.docIdName;
    if (searchParams.statusCode) params['statusCode'] = searchParams.statusCode;

    if (searchParams.product) params['productId'] = searchParams.product.id;

    params['includeInactiveSw'] = searchParams.includeInactiveSw || false;

    if (searchParams.searchId) params['searchId'] = searchParams.searchId;
    if (searchParams.rangeFrom != null) params['fromRec'] = searchParams.rangeFrom;
    if (searchParams.rangeTo != null) params['toRec'] = searchParams.rangeTo;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json',
      })
      .pipe(
        map((data) => this.extractGetRevDocs(data, dispatchDocList, info, newSearch)),
        catchError((error) => {
          return this.authService.handleError(error, info);
        })
      );

    return response;
  }

  private _extractSingleRevDoc(res: any, info: ServiceCallInfo) {

    this.authService.extractRequestStatus(info, res);
    let result: RevDoc = null;
    let data = res.reply.requestResult.data;
    if (data) {
      let dataTag = data.revDocs;
      if (dataTag) {
        let arr = new Array().concat(dataTag);
        arr.forEach((i) => {
          result = (this._extractRevDocTag(i));
        });
      }
  }
  return result;
}

  private extractGetRevDocs(
    res: any,
    dispatchDocList,
    info: ServiceCallInfo,
    newSearch
  ): RevDoc[] {
    console.log(res);
    this.authService.extractRequestStatus(info, res);
    let result: RevDoc[] = [];
    let data = res.reply.requestResult.data;
    let metaData = new ServiceMetaData();

    if (data.metadata) {
      let metaTag = data.metadata;
      metaData.totalRecords = metaTag.totRec;
      metaData.fromRecord = metaTag.fromRec;
      metaData.toRecord = metaTag.toRec;
      metaData.searchId = metaTag.searchId;
    }

    if (data) {
      let dataTag = data.revDocs;
      if (dataTag) {
        let arr = new Array().concat(dataTag);
        arr.forEach((i) => {
          result.push(this._extractRevDocTag(i));
        });
      }

      if (!metaData.searchId && result) {
        metaData.fromRecord = 1;
        metaData.toRecord = result.length;
        metaData.totalRecords = res.reply.requestResult.data.totalCount;
      }
    }

    result.srp_sortByKey('docId', -1);

    if (dispatchDocList) {
      if (newSearch) {
        this.ngRedux.dispatch({
          type: actionsList.GET_REV_DOCS,
          data: result,
          reply: res,
          metaData: metaData
        });
      } else {
        this.ngRedux.dispatch({
          type: actionsList.ADD_REV_DOCS,
          data: result,
          reply: res,
          metaData: metaData,
        });
      }
    }

    return result;
  }

  _extractRevDocTag(i: any): RevDoc {
    let d = new RevDoc();
    d.docId = i.docId;
    d.docName = i.docName;
    d.lastVersionId = i.versionId;
    // d.createDateFormatted = this.datePipe.transform(
    //   i.crDate,
    //   SharedService.appDateFormat,
    //   this.authService.currentUser.timezone
    // );

    d.createDateFormatted = this.authService.formatDBDateTime(i.crDate);

    d.productId = i.productId;
    d.productDescr = i.productDescr;
    d.catId = i.catId;
    d.catDescr = i.catDescr;
    if (i.catVersion) {
      d.catVersionId = i.catVersion.catVersionId;
      d.actCatVersionId = i.catVersion.catVersionId;
    }

    d.newCatVersionSw = this.authService.fromXMLBoolean(i.newCatVersionSw);
    d.fileName = i.fileName;
    d.docType = i.docType;
    d.docS3Key = i.docS3Key;
    d.inddS3Key = i.inddS3Key;
    d.pagesNum = i.pageNums;
    d.statusCode = i.revDocStatusCode;
    d.statusDescr = i.revDocStatusDescr;
    d.statusDate = this.authService.formatDBDateTime(i.revDocStatusDate);
    d.runStatusCode = i.runStatusCode;
    d.runStatusMessage = i.runStatusMessage;
    d.runNum = i.runNum;
    d.parentDocId = i.parentDocId;
    d.uploadUserDescr = i.uploadUserDescr;
    if (i.assetType) d.assetType = new DataElement(i.assetType, i.assetDescr);
    d.disableCanvasViewer = i.disableCanvasViewer || false;
    d.catSystemVersion = i.catSystemVersion;

    let vArr = new Array().concat(i.versions);
    vArr.forEach((v) => {
      let version = new RevDoc();
      version.docId = v.docId;
      version.versionId = v.versionId;
      version.statusCode = v.revDocStatusCode;
      version.statusDescr = v.revDocStatusDescr;
      version.statusDate = this.authService.formatDBDateTime(
        v.revDocStatusDate
      );
      version.fileName = v.fileName;
      version.docType = v.docType;
      version.docS3Key = v.docS3Key;
      version.inddS3Key = v.inddS3Key;
      version.pagesNum = v.pageNums;
      version.docName = v.docName;
      version.createDateFormatted = this.authService.formatDBDateTime(v.crDate);
      version.productId = v.productId;
      version.productDescr = v.productDescr;
      version.catId = v.catId;
      version.catDescr = v.catDescr;
      //version.catVersionId = v.catVersionId;
      if (v.catVersion) {
        version.catVersionId = v.catVersion.catVersionId;
        version.actCatVersionId = v.catVersion.catVersionId;
      }
      version.runStatusCode = v.runStatusCode;
      version.runNum = v.runNum;
      version.catSystemVersion = v.catSystemVersion;
      version.uploadUserDescr = v.uploadUserDescr;
      if (v.assetType)
        version.assetType = new DataElement(v.assetType, v.assetDescr);

      if (v.pages) {
        version.pages = [];
        let pArr = new Array().concat(v.pages);
        pArr.forEach((p) => {
          let page = new DocPage();
          page.bucket = p.bucket;
          page.pageNum = p.dispPageNum;
          page.s3Key = p.s3Key;
          page.url = p.url;

          version.pages.push(page);
        });
      }
      version.disableCanvasViewer = v.disableCanvasViewer || false;

      d.docVersions.push(version);
    });

    d.docVersions.srp_sortByKey('versionId', -1);
    return d;
  }

  getRevDocStatusList(): Observable<DataElement[]> {
    let serviceName = 'get_rev_doc_status_list_func';

    /*if (this.mockupSw) {
      return this.http
        .get(this.mockupDataUrl + 'get_rev_doc_status_list.json')
        .pipe(
          map((res) => this.extractRevDocStatusList(res)),
          catchError((error) => {
            return this.authService.handleError(error, info);
          })
        );
    }*/

    let response = new Observable<DataElement[]>();
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);

    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json',
      })
      .pipe(
        take(1),
        map((data) => {
          return this.extractRevDocStatusList(data, info);
        }),
        catchError((err) => {
          return this.authService.handleError(err, info);
        })
      );
    return response;
  }

  private extractRevDocStatusList(res, info: ServiceCallInfo): DataElement[] {
    const results: DataElement[] = this.authService.mapToDataElement(
      res,
      'revDocStatusCode',
      'descr',
      'revdocStatus',
      info
    );

    this.ngRedux.dispatch({
      type: actionsList.GET_REV_DOC_STATUS_LIST,
      data: results,
      reply: res,
    });

    return results;
  }

  getRevRulesLight(docId: number, pageNum: number, searchParams: RevRulesSearchParams): Observable<RevRuleCacheObject> {
    let serviceName = 'get_rev_rules_func';

    let response = new Observable<RevRuleCacheObject>();
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params['docId'] = docId;
    if (pageNum) params['dispPageNum'] = pageNum;

    if (searchParams) {
      if (searchParams.phrase) 
        params['phraseText'] = searchParams.phrase;
      if (searchParams.ruleStatus)
        params['revRuleStatusCode'] = searchParams.ruleStatus.id;
      if (searchParams.contentType)
        params['revRuleContentType'] = searchParams.contentType.id;
      if (searchParams.matchType)
        params['revRuleMatchType'] = searchParams.matchType.id;
      if (searchParams.ruleType)
        params['revRuleTypeCode'] = searchParams.ruleType.id;
      if (searchParams.subTypeList)
        params['subTypeList'] = searchParams.subTypeList;
      if (searchParams.currLoadedRuleNum != undefined) {
        params['fromRec'] = searchParams.currLoadedRuleNum;
        params['toRec'] = searchParams.currLoadedRuleNum + 10;
      }
    }
    if (this.ngRedux.getState().revRuleIgnoredStatusCodes.length > 0) {
      params['revRuleIgnoredStatusCodes'] = this.mapRevRuleIgnoredStatusCodes();
    }

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json',
      })
      .pipe(
        map((data) => this.extractRevRules(data, info)),
        catchError((error) => {
          return this.authService.handleError(error, info);
        })
      );

    return response;
  }

  getRevRules(
    docId: number,
    pageNum: number,
    searchParams: RevRulesSearchParams
  ): Observable<number> {
    let serviceName = 'get_rev_rules_func';

    let response = new Observable<number>();
    let params = new ServiceOptions();
    params.serviceName = serviceName;
    params.urldecodeSw = false;

    params['docId'] = docId;
    if (pageNum) params['dispPageNum'] = pageNum;

    if (searchParams) {
      if (searchParams.phrase) params['phraseText'] = searchParams.phrase;
      if (searchParams.ruleStatus)
        params['revRuleStatusCode'] = searchParams.ruleStatus.id;
      if (searchParams.contentType)
        params['revRuleContentType'] = searchParams.contentType.id;
      if (searchParams.matchType)
        params['revRuleMatchType'] = searchParams.matchType.id;
      if (searchParams.ruleType)
        params['revRuleTypeCode'] = searchParams.ruleType.id;
      if (searchParams.subTypeList)
        params['subTypeList'] = searchParams.subTypeList;
      if (searchParams.currLoadedRuleNum != undefined) {
        params['fromRec'] = searchParams.currLoadedRuleNum;
        params['toRec'] = searchParams.currLoadedRuleNum + 10;
      }
    }
      if (this.ngRedux.getState().revRuleIgnoredStatusCodes.length > 0) {
        // map rules
        params['revRuleIgnoredStatusCodes'] = this.mapRevRuleIgnoredStatusCodes();
        
      }

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);

    return this.authService.runGeneralSearch(body);
  }

  getRevRulesSearch(searchId: number): Observable<RevRuleCacheObject> {
    let serviceName = 'get_search_results_func';
    let response = new Observable<RevRuleCacheObject>();
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params['searchId'] = searchId;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json',
      })
      .pipe(
        map((data) => this.extractRevRules(data, info)),
        catchError((error) => {
          return this.authService.handleError(error, info);
        })
      );

    return response;
  }

  getRevRulesSearchWithoutDuplicates(searchId: number): Observable<RevRuleCacheObject> {
    return this.getRevRulesSearch(searchId).pipe(
      map((revRuleCacheObject: RevRuleCacheObject) => {
        revRuleCacheObject.revRules.map((rule: any) => {
          if (rule.referencedBy) {
            rule.referencedBy = this.removeDuplicatesFromPhrases(rule.referencedBy);
          }
          if (rule.references) {
            rule.references = this.removeDuplicatesFromPhrases(rule.references);
          }
          return rule;
        })
        return revRuleCacheObject;

        }
      
      )
    );
  }
  public getRevIgnoredStatus(docId: number): Observable<string | null> {

    let url = `${this.scaiServiceUrl}rev-docs/${docId}`;
    return this.http
      .get(url, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json',
      })
      .pipe(
        take(1),
        map((data) => {
          
          return data['revRuleIgnoredStatusCodes'];
        }),
        catchError((err) => {
          
          return of(null);
        }
                   
        )
      );
  }
  public setRevIgnoredStatus(docId: number, revRuleIgnoredStatusCode: string | null) {
        let url = `${this.scaiServiceUrl}rev-docs/${docId}`;
    return this.http
      .patch(url,  { revRuleIgnoredStatusCodes: revRuleIgnoredStatusCode}, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json',
      })
      .pipe(
        take(1),
        map((data: string | null) => {
          
          return data;
        }),
        catchError((err) => {
          
          return of(null);
        }
                   
        )
      );
  }
  
  private removeDuplicatesFromPhrases(phrases: any[] = []): any[] {
    const uniqueRefTxt = new Set();
    return phrases.filter(phrase => {
      const isDuplicate = uniqueRefTxt.has(phrase.refPhraseText);
      uniqueRefTxt.add(phrase.refPhraseText);
      return !isDuplicate;
    });
  }

  private extractRevRules(res: any, info: ServiceCallInfo): RevRuleCacheObject {
    this.authService.extractRequestStatus(info, res);
    let result: RevRule[] = [];
    let data = res.reply.requestResult.data;
    let revRuleCacheObject: RevRuleCacheObject = {
      revRules: [],
      reply: res
    }
    if (data) {
      //totalCount
      const totalNumPhrases = data.totalCount || 0;
      this.ngRedux.getState().currLoadedRuleNum  += 10;
    this.ngRedux.dispatch({
      type: actionsList.GET_TOTAL_FILTER_RULES_NUM,
      data: totalNumPhrases
    });
      let dataTag = data.revRule;
      if (dataTag) {
        let arr = new Array().concat(dataTag);
        arr.forEach((i) => {
          result.push(this.catService._extractRevRuleTag(i));
        });
        // If any rules have associatedImages, propagate their imgUrl to the corresponding RevRule
        try {
          for (const parentRule of result) {
            if (!parentRule.associatedImages) continue;
            for (const assocImg of parentRule.associatedImages) {             
              const matchRule = result.find(r => r.imageId === assocImg.assocImageId);
              if (matchRule) {
                if (assocImg.imgUrl) {
                  matchRule.imgUrl = assocImg.imgUrl;
                } else if (assocImg.imgS3Key) {
                  this.catService.assignOrSubscribeObjectForPresignedImgUrl(matchRule, assocImg.imgS3Key);
                }
              }
            }
          }
        } catch (e) {
          // don't block extraction on mapping errors
          console.warn('Error mapping associatedImages to RevRule.imgUrl', e);
        }
        this.catService.resolveAnyRemainingPresignedUrl();
        if (Config.assetConfigData['showChildrenTiles']) {
          result = this._extractChildren(result);
        } else {
          result.forEach(res => res.isParentActionSelected = true);
        }
        revRuleCacheObject.revRules = result;
      }

      let statsTag = data.revStats;
      if (statsTag) {
        this.updRevDocStats(statsTag, true);
      }
    }

    // result.srp_sortByKey('revRuleId');
/* 
    this.ngRedux.dispatch({
      type: actionsList.GET_REV_DOC_RULES,
      data: result,
      reply: res,
    }); */

    return revRuleCacheObject;
  }

  getRevRuleTypeList(): Observable<DataElement[]> {
    let serviceName = 'get_rev_rule_type_list_func';

    let response = new Observable<DataElement[]>();
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);

    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json',
      })
      .pipe(
        take(1),
        map((data) => {
          return this.extractRevRuleTypes(data, info);
        }),
        catchError((err) => {
          return this.authService.handleError(err, info);
        })
      );
    return response;
  }

  private extractRevRuleTypes(res, info: ServiceCallInfo): DataElement[] {
    const results: DataElement[] = this.authService.mapToDataElement(
      res,
      'revDocRuleTypeCode',
      'descr',
      'revRuleType',
      info
    );

    this.ngRedux.dispatch({
      type: actionsList.GET_REV_RULE_TYPES,
      data: results,
      reply: res,
    });

    return results;
  }

  getRevCatalogList(product: Product | null = null): Observable<RevCatalog[]> {
    let serviceName = 'get_rev_catalog_list_func';

    /*if (this.mockupSw) {
      return this.http
        .get(this.mockupDataUrl + 'get_rev_catalog_list.json')
        .pipe(
          map((res) => this.extractRevCatalogList(res)),
          catchError((error) => {
            return this.authService.handleError(error, info);
          })
        );
    }*/

    let response = new Observable<RevCatalog[]>();
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params['envId'] = this.ngRedux.getState().curEnv.id;
    if (product) {
      params['productId'] = Number.parseInt(product.id);
    }
    
    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json',
      })
      .pipe(
        take(1),
        map((data) => {
          return this.extractRevCatalogList(data, info);
        }),
        catchError((err) => {
          return this.authService.handleError(err, info);
        })
      );
    return response;
  }

  private extractRevCatalogList(res, info: ServiceCallInfo): RevCatalog[] {
    this.authService.extractRequestStatus(info, res);
    let result: RevCatalog[] = [];
    let data = res.reply.requestResult.data;

    if (data && data.cat) {
      let arr = new Array().concat(data.cat);
      arr.forEach((i) => {
        let c = new RevCatalog();
        c.catId = i.catId;
        c.catDescr = i.descr;
        c.lastVersionId = i.lastVersionId;
        c.catVersionId = i.catVersionId;
        c.catType = i.catType;
        c.catTypeDescr = i.catTypeDescr;
        c.systemVersion = i.catSystemVersion;
        c.catAssetTypeList = [];
        if (i.catAssetTypes) {
          let assetTypeArr = new Array().concat(i.catAssetTypes);
          assetTypeArr.forEach((t) => {
            c.catAssetTypeList.push(new DataElement(t.assetType, t.assetDescr));
          });
        }

        result.push(c);
      });
    }

    return result;
  }

  updRevDoc(revDoc: RevDoc, statusCode: string): Observable<RevDoc> {
    let serviceName = 'upd_rev_doc_func';

    /*if (this.mockupSw) {
      return this.http.get(this.mockupDataUrl + 'update_result.json').pipe(
        map((res) => this.authService.extractRequestStatus(res, serviceName)),
        catchError((error) => {
          return this.authService.handleError(error, info);
        })
      );
    }*/

    let response = new Observable<RevDoc>();
    let params = new ServiceOptions();
    params.serviceName = serviceName;
    params['docId'] = revDoc.docId;
    params['revDocStatusCode'] = statusCode;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json',
      })
      .pipe(
        map((data) => {
          return this.extractUpdRevDoc(data, info);
        }),
        catchError((err) => {
          return this.authService.handleError(err, info);
        })
      );
    return response;
  }

  extractUpdRevDoc(res: any, info: ServiceCallInfo): RevDoc {
    this.authService.extractRequestStatus(info, res);
    let data = res.reply.requestResult.data;
    let result: RevDoc;

    if (data && data.revDoc) {
      result = this._extractRevDocTag(data.revDoc);
    }

    this.ngRedux.dispatch({
      type: actionsList.UPD_REV_DOC,
      data: result,
      reply: res,
    });

    return result;
  }

  processUpdRevDocResponse(response: any): RevDoc {
    const data = response?.reply?.requestResult?.data;
    let result: RevDoc = null;

    if (data?.revDoc) {
      result = this._extractRevDocTag(data.revDoc);
    }

    this.ngRedux.dispatch({
      type: actionsList.UPD_REV_DOC,
      data: result,
      reply: response,
    });

    return result;
  }

  updRevRule(
    revDoc: RevDoc,
    revRules: RevRule[],
    status: RevStatus,
    contextCatPhraseId: number = null
  ): Observable<RevRule[]> {
    let serviceName = 'upd_rev_rule_func';

    /*if (this.mockupSw) {
      return this.http.get(this.mockupDataUrl + 'update_result.json').pipe(
        map((res) => this.authService.extractRequestStatus(res, serviceName)),
        catchError((error) => {
          return this.authService.handleError(error, info);
        })
      );
    }*/

    let response = new Observable<RevRule[]>();
    let params = new ServiceOptions();
    params.serviceName = serviceName;
    params['docId'] = revDoc.docId;
    params['revRuleIds'] = revRules.map((r) => r.revRuleId);
    params['revRuleStatusCode'] = status;
    params['contextCatPhraseId'] = contextCatPhraseId;
    
    if (this.ngRedux.getState().revRuleIgnoredStatusCodes.length > 0) {
        // map rules
        params['revRuleIgnoredStatusCodes'] = this.mapRevRuleIgnoredStatusCodes();
        
    }
    if (revRules.length == 1) {
      params['revRuleComment'] = revRules[0].comment;
      if (
        revRules[0].revRuleType == 'CAT_MATCH' &&
        revRules[0].ruleCatMatches.length > 0
      ) {
        let idx = revRules[0].ruleCatMatches.findIndex(
          (m) => m.reportDisplaySw
        );
        if (idx != -1)
          params['reportDisplayCatMatchSeq'] =
            revRules[0].ruleCatMatches[idx].catMatchSeq;
      }
    }

    params.urldecodeSw = true;

    // let cards: Card[] = [];
    // revRules.forEach((r) => {
    //   if (
    //     r.cardEntryMatch &&
    //     cards.findIndex((c) => c.cardId == r.cardEntryMatch.card.cardId) == -1
    //   ) {
    //     cards.push(r.cardEntryMatch.card);
    //   }
    // });

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json',
      })
      .pipe(
        map((data) => {
          return this.extractUpdRevRule(data, info);
        }),
        catchError((err) => {
          return this.authService.handleError(err, info);
        })
      );
    return response;
  }

  private extractUpdRevRule(
    res,
    //cards: Card[],
    info: ServiceCallInfo
  ): RevRule[] {
    this.authService.extractRequestStatus(info, res);
    let data = res.reply.requestResult.data;
    let results: RevRule[] = [];

    if (data) {
      let rulesTag = data.revRule;
      if (rulesTag) {
        let arr = new Array().concat(rulesTag);
        arr.forEach((i) => {
          results.push(this.catService._extractRevRuleTag(i));
        });
      }

      let statsTag = data.revStats;
      if (statsTag) {
        this.updRevDocStats(statsTag);
      }
    }

    return results;
  }

  updRevDocStats(statsTag: any, saveToStore: boolean = true) {
    let passedCnt = 0;
    let failedCnt = 0;
    let dismissedCnt = 0;
    let markedForChangesCnt = 0;
    let notPassedVarCnt = 0;
    let notPassedSevCnt = 0;
    let newContentCnt = 0;
    let stetCnt = 0;
    let newClaimCnt = 0;
    let claimVariantCnt = 0;
    let progressPct;
    let totCnt = statsTag.totCnt || 0;

    let statsArr = new Array().concat(statsTag.revStatusStats || []);
   
    statsArr.forEach((s) => {
      switch (s.revRuleStatusCode) {
        case RevStatus.PASSED:
          passedCnt = s.cnt;
          break;
        case RevStatus.NOT_PASSED:
          failedCnt = s.cnt;
          break;
        case RevStatus.DISMISSED:
          dismissedCnt = s.cnt;
          break;
        case RevStatus.MARKED_FOR_CHANGES:
          markedForChangesCnt = s.cnt;
          break;
        case RevStatus.NOT_PASSED_VAR:
          notPassedVarCnt = s.cnt;
          break;
        case RevStatus.NOT_PASSED_SEV:
          notPassedSevCnt = s.cnt;
          break;
        case RevStatus.NEW_CONTENT:
          newContentCnt = s.cnt;
          break;
        case RevStatus.STET:
          stetCnt = s.cnt;
          break;
        case RevStatus.NEW_CLAIM:
          newClaimCnt = s.cnt;
          break;
        case RevStatus.CLAIM_VARIANT:
          claimVariantCnt = s.cnt;
          break;
      }
    });

    progressPct = statsTag.progressPct || 0;
    if (saveToStore) {
      this.ngRedux.dispatch({
        type: actionsList.UPD_REV_DOC_STATS,
        data: {
          passedCnt: passedCnt,
          failedCnt: failedCnt,
          dismissedCnt: dismissedCnt,
          markedCnt: markedForChangesCnt,
          stetCnt: stetCnt,
          notPassedVarCnt: notPassedVarCnt,
          notPassedSevCnt: notPassedSevCnt,
          newContentCnt: newContentCnt,
          progressPct: progressPct,
          totCnt: totCnt,
          newClaimCnt: newClaimCnt,
          claimVariantCnt: claimVariantCnt
        }
      });
    }
  }

  createRevDoc(revDoc: RevDoc): Observable<RevDoc> {
    let serviceName = 'create_rev_doc_func';

    let response = new Observable<RevDoc>();
    let params = new ServiceOptions();
    params.serviceName = serviceName;
    params['envId'] = this.ngRedux.getState().curEnv.id;
    params['docName'] = revDoc.docName;
    params['parentDocId'] = revDoc.parentDocId;
    params['productId'] = Number.parseInt(revDoc.productId);
    params['catId'] = revDoc.catId;
    params['catVersionId'] = revDoc.catVersionId;
    params['docS3Key'] = revDoc.docS3Key;
    params['fileName'] = revDoc.fileName;
    if (revDoc.metadata) {
      params['metadata'] = revDoc.metadata;
    }
    if (revDoc.assetType) {
      params['assetType'] = Number.parseInt(revDoc.assetType.id);
    }
    // params['algRunType'] = 'APRIMO_TEST';

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json',
      })
      .pipe(
        map((data) => {
          return this.extractCreateRevDoc(data, info);
        }),
        catchError((err) => {
          return this.authService.handleError(err, info);
        })
      );
    return response;
  }

  extractCreateRevDoc(res: any, info: ServiceCallInfo): RevDoc {
    this.authService.extractRequestStatus(info, res);
    let data = res.reply.requestResult.data;
    let result: RevDoc;

    if (data) {
      result = this._extractRevDocTag(data);
    }

    return result;
  }

  getRevRuleStatusList(): Observable<DataElement[]> {
    let serviceName = 'get_rev_rule_status_list_func';

    let response = new Observable<DataElement[]>();
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);

    if (this.ngRedux.getState().revRuleStatusList.length > 0) {
      this.updateRevRulesStatusNlsStrings();
      return of(this.ngRedux.getState().revRuleStatusList);
    }

    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json',
      })
      .pipe(
        take(1),
        map((data) => {
          return this.extractRevRuleStatusList(data, info);
        }),
        catchError((err) => {
          return this.authService.handleError(err, info);
        })
      );
    return response;
  }

  private extractRevRuleStatusList(res, info: ServiceCallInfo): DataElement[] {
    const results: DataElement[] = this.authService.mapToDataElement(
      res,
      'revRuleStatusCode',
      'descr',
      'revRuleStatus',
      info
    );

    this.ngRedux.dispatch({
      type: actionsList.GET_REV_RULE_STATUS_LIST,
      data: results,
      reply: res,
    });

    this.updateRevRulesStatusNlsStrings();

    return results;
  }

  private updateRevRulesStatusNlsStrings() {
    this.translateService.set(
      'translate.general.passedCnt',
      this.ngRedux
        .getState()
        .revRuleStatusList.find((s) => s.id == RevStatus.PASSED).text,
      this.ngRedux.getState().ui_lang
    );
    this.translateService.set(
      'translate.general.failedCnt',
      this.ngRedux
        .getState()
        .revRuleStatusList.find((s) => s.id == RevStatus.NOT_PASSED).text,
      this.ngRedux.getState().ui_lang
    );
    this.translateService.set(
      'translate.general.dismissedCnt',
      this.ngRedux
        .getState()
        .revRuleStatusList.find((s) => s.id == RevStatus.DISMISSED).text,
      this.ngRedux.getState().ui_lang
    );
    this.translateService.set(
      'translate.general.markedCnt',
      this.ngRedux
        .getState()
        .revRuleStatusList.find((s) => s.id == RevStatus.MARKED_FOR_CHANGES)
        .text,
      this.ngRedux.getState().ui_lang
    );
    this.translateService.set(
      'translate.general.stetCnt',
      this.ngRedux
        .getState()
        .revRuleStatusList.find((s) => s.id == RevStatus.STET).text,
      this.ngRedux.getState().ui_lang
    );
    this.translateService.set(
      'translate.general.notPassedVarCnt',
      this.ngRedux
        .getState()
        .revRuleStatusList.find((s) => s.id == RevStatus.NOT_PASSED_VAR).text,
      this.ngRedux.getState().ui_lang
    );
    this.translateService.set(
      'translate.general.notPassedSevCnt',
      this.ngRedux
        .getState()
        .revRuleStatusList.find((s) => s.id == RevStatus.NOT_PASSED_SEV).text,
      this.ngRedux.getState().ui_lang
    );
    this.translateService.set(
      'translate.general.newClaimCnt',
      this.ngRedux
        .getState()
        .revRuleStatusList.find((s) => s.id == RevStatus.NEW_CLAIM).text,
      this.ngRedux.getState().ui_lang
    );
    this.translateService.set(
      'translate.general.claimVariantCnt',
      this.ngRedux
        .getState()
        .revRuleStatusList.find((s) => s.id == RevStatus.CLAIM_VARIANT).text,
      this.ngRedux.getState().ui_lang
    );
  }

  createAnnotateFileReq(doc: RevDoc): Observable<number> {
    let serviceName = 'create_annotate_file_req_func';

    let response = new Observable<number>();
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params['docId'] = doc.docId;
    params['catVersionId'] = doc.catVersionId;
    params['docS3Key'] = doc.docS3Key;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json',
      })
      .pipe(
        map((data) => {
          return this.extractAnnotateFileReq(data, info);
        }),
        catchError((err) => {
          return this.authService.handleError(err, info);
        })
      );
    return response;
  }

  extractAnnotateFileReq(res: any, info: ServiceCallInfo): number {
    this.authService.extractRequestStatus(info, res);
    let data = res.reply.requestResult.data;
    if (data) {
      return data.runNum;
    }

    return 0;
  }

  processAnnotatePrecheck(runNum: number): Observable<boolean> {
    let response = new Observable<boolean>();
    let serviceName = 'ScaiPyAdmin.StartDocProcess';
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params['runNum'] = runNum;
    //params['username'] = this.ngRedux.getState().user.username;
    params['userId'] = this.ngRedux.getState().user.userId;
    params['token'] = this.ngRedux.getState().user.token;

    let url = this.authService.getRequestUrl(serviceName, true);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json',
      })
      .pipe(
        map((data) => this.authService.extractRequestStatus(info, data)),
        catchError((error) => {
          return this.authService.handleError(error, info);
        })
      );

    return response;
  }

  getRevCards(docId: number): Observable<number> {
    let serviceName = 'get_rev_cards_func';
    let params = new ServiceOptions();
    params.serviceName = serviceName;
    params.urldecodeSw = false;
    params['docId'] = docId;
    let body = this.authService.getRequestBody(params);
    return this.authService.runGeneralSearch(body);
  }

  getRevCardsSearch(searchId: number): Observable<void> {
    let serviceName = 'get_search_results_func';
    let response = new Observable<void>();
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params['searchId'] = searchId;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json',
      })
      .pipe(
        map((data) => this.extractRevCards(data, info)),
        catchError((error) => {
          return this.authService.handleError(error, info);
        })
      );

    return response;
  }

  private extractRevCards(res: any, info: ServiceCallInfo) {
    this.authService.extractRequestStatus(info, res);
    let data = res.reply.requestResult.data;

    if (data) {
      let mcCards: Card[] = [];
      let mcTag = data.catCards;
      if (mcTag) {
        let arr = new Array().concat(mcTag);
        arr.forEach((i) => {
          let card = this.catService._extractCardTag(i);
          mcCards.push(card);
        });
      }
      this.ngRedux.dispatch({
        type: actionsList.GET_REV_DOC_MATCHED_CARDS,
        data: mcCards,
        reply: res,
      });

      let revCards: Card[] = [];
      let rcTag = data.revCards;
      if (rcTag) {
        let arr = new Array().concat(rcTag);
        arr.forEach((i) => {
          let card = this.catService._extractCardTag(i);
          card.matchedCard = mcCards.find(
            (c) => card.matchedCardId == c.cardId
          );
          revCards.push(card);
        });
      }
      this.ngRedux.dispatch({
        type: actionsList.GET_REV_DOC_CARDS,
        data: revCards,
        reply: res,
      });

      let cardMatches: CardMatch[] = [];
      let cmTag = data.cardMatch;
      if (cmTag) {
        let arr = new Array().concat(cmTag);
        arr.forEach((i) => {
          let match = this.catService._extractRevCardMatchTag(i);
          cardMatches.push(match);
        });
      }

      this.ngRedux.dispatch({
        type: actionsList.GET_REV_CARD_MATCHES,
        data: cardMatches,
        reply: res,
      });

      let imageMatches: ImageMatch[] = [];
      let imTag = data.imageMatch;

      if (imTag) {
        let arr = new Array().concat(imTag);
        arr.forEach((i) => {
          let match = this.catService._extractRevImageMatchTag(i); // Assumes a method to extract and map the image match
          imageMatches.push(match);
        });
      }

      this.ngRedux.dispatch({
        type: actionsList.GET_REV_IMAGE_MATCHES,
        data: imageMatches,
        reply: res,
      });

      this.ngRedux.getState().revDocRules.forEach((r) => {
        if (r.cardMatchId) this.catService._setRuleCardMatch(r);
      });

      this.ngRedux.dispatch({
        type: actionsList.GET_REV_DOC_CARDS_END,
        data: cardMatches,
        reply: res,
      });
    }
  }

  getRevRuleSingle(docId: number, phraseOrImageId: number, isImage: boolean = false, silent: boolean = false, srcRule?: RevRule): Observable<RevRule> {
    let serviceName = 'get_rev_rules_func';

    let response = new Observable<RevRule>();
    let params = new ServiceOptions();
    params.serviceName = serviceName;
    params.urldecodeSw = false;

    params['docId'] = docId;
    if (isImage) {
      params['imageId'] = phraseOrImageId;
    } else {
      params['phraseId'] = phraseOrImageId;
    }

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json',
      })
      .pipe(
        map((res: any) => {
          this.authService.extractRequestStatus(info, res);
          let result: RevRule[] = [];
          let data = res.reply.requestResult.data;
          if (data) {
            let dataTag = data.revRule;
            if (dataTag) {
              let arr = new Array().concat(dataTag);
              arr.forEach((i) => {
                const rule = this.catService._extractRevRuleTag(i);
                rule.fullyFetched = true;
                result.push(rule);
              });
            }
          }

          let revRule = result[0];
          if (revRule && srcRule) {
            if (Config.assetConfigData['showChildrenTiles']) {
              revRule = this.setRuleChildren(srcRule, revRule);
            } else {
              revRule.isParentActionSelected = true;
            }
          }

          return revRule;
        }),
        catchError((error) => {
          if (silent) {
            return throwError(error);
          }
          return this.authService.handleError(error, info);
        })
      );

    return response;
  }

  getRevRuleSingleImage(docId: number, imageId: number): Observable<RevRule> {
    let serviceName = 'get_rev_rules_func';

    let response = new Observable<RevRule>();
    let params = new ServiceOptions();
    params.serviceName = serviceName;
    params.urldecodeSw = false;

    params['docId'] = docId;
    params['imageId'] = imageId;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json',
      })
      .pipe(
        map((res: any) => {
          this.authService.extractRequestStatus(info, res);
          let result: RevRule[] = [];
          let data = res.reply.requestResult.data;
          if (data) {
            let dataTag = data.revRule;
            if (dataTag) {
              let arr = new Array().concat(dataTag);
              arr.forEach((i) => {
                result.push(this.catService._extractRevRuleTag(i));
              });
            }
          }
          return result[0];
        }),
        catchError((error) => {
          return this.authService.handleError(error, info);
        })
      );

    return response;
  }
 
  renameRevDoc(docId: number, docName: string): Observable<boolean> {
    let url = `${this.scaiServiceUrl}rev-docs/${docId}`;
    return this.http
      .patch(url, { docName }, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json',
      })
      .pipe(
        take(1),
        map((data: any) => {
          return true;
        }),
        catchError((err) => {
          return this.handleError(err, url);
        })
      );
  }

  private handleError(error: any, url: string): Observable<never> {
    const info = new ServiceCallInfo(url, url, null);
    error.message = error.error.description || error.statusText;

    return this.authService.handleError(error, info);
  }

  getRevDocPagePhrases(docId: number, pageNum: number): Observable<PageToolRevPhrase[]> {
    let url = this.scaiServiceUrl + `/page-phrases?doc_id=${docId}&page_num=${pageNum}&is_precheck=true`;
    return this.http
      .get<PageToolRevPhrase[]>(url, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json'
      }).pipe(
        map((phrases: PageToolRevPhrase[]) => {
          return phrases.sort((a, b) => a.page_order_id - b.page_order_id);
        }),
        catchError((err) => {
          return this.handleError(err, url)
        })
      );
  }

  getRevDocPageImages(docId: number, pageNum: number): Observable<PageToolRevImage[]> {
    let url = this.scaiServiceUrl + `/page-images?doc_id=${docId}&page_num=${pageNum}&is_precheck=true`;
    return this.http
      .get<PageToolRevImage[]>(url, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json'
      }).pipe(
        map((images: PageToolRevImage[]) => {
          images.forEach(image => {
            this.catService.assignOrSubscribeObjectForPresignedImgUrl(image, image.s3_key);
          });
          return images.sort((a, b) => a.page_order_id - b.page_order_id);
        }),
        catchError((err) => {
          return this.handleError(err, url)
        })
      );
  }

  private _extractChildren(results: RevRule[]) {
    // iterate over all results
    let rulesToDelete: {isImage: boolean, id: number}[] = [];
    for (let i=0; i<results.length; i++) {
      
      let parentPhrase: RevRule = results[i];

      // get all children for current phrase
      for (let j=0; j<results.length; j++) {
          let childPhrase: RevRule = results[j];
          if (childPhrase.phraseId == parentPhrase.phraseId) {
            continue;
          }

          if (childPhrase.parentPhraseId === parentPhrase.phraseId) {
            
            let contentType = childPhrase.revRuleContentTypeDesc;
            let revRuleArr: RevRule[] | null = null;
            
            if (contentType == 'Footnote') {
              
              contentType = 'Footnotes';
            }
            if (contentType == 'Table') {
              
              contentType = 'Tables';
            }
            if (contentType == 'Claim content') {
              
              contentType = 'Content';
              revRuleArr = parentPhrase.contentChildren;
            }
            if (contentType == 'Image') {
              
              contentType = 'Images';
              revRuleArr = parentPhrase.imageChildren;
            }
            if (revRuleArr == null) {
              if (parentPhrase.children[contentType] == undefined) {
                parentPhrase.children[contentType] = [];
              }
              revRuleArr = parentPhrase.children[contentType];
            }
            if (childPhrase.phraseId != -1) {
              
              rulesToDelete.push({isImage: false, id: childPhrase.phraseId});
            } else {
              rulesToDelete.push({isImage: true, id: childPhrase.imageId});
            }
            
            childPhrase.isParentActionSelected = (parentPhrase.ruleUserStatus != undefined);

            revRuleArr.push(childPhrase);
            // remove from associated text, associated images or references
            if (!childPhrase.isImage) {
              
              let associatedText = parentPhrase.associatedText?.find(res =>  (res.assocPhraseId == childPhrase.phraseId));

              if (associatedText) {
                
                associatedText.showInTile = false;
              }
              
              let reference = parentPhrase.references?.find(res => (res.refPhraseId == childPhrase.phraseId));

              if (reference) {
                
                reference.showInTile = false;
              }
            } else {
              
              let associatedImage = parentPhrase.associatedImages?.find(res =>  (res.assocImageId == childPhrase.imageId));

              if (associatedImage) {

                associatedImage.showInTile = false;
              }
            }
            
            let associatedBy = childPhrase.associatedBy?.find(phrase =>  phrase.phraseId == parentPhrase.phraseId);

            if (associatedBy) {
              associatedBy.showInTile = false;
            }
            
            let referencedBy = childPhrase.referencedBy?.find(phrase => (phrase.phraseId == parentPhrase.phraseId));
            if (referencedBy) {

              referencedBy.showInTile = false;
            }
          }   

      }
    
      if (parentPhrase.references) {

        //filter suggested References 
        for (let y=0; y<parentPhrase.references?.length; y++) {
          let reference = parentPhrase.references[y];
          if (reference.isConsistencyGenerated) {
              // reference.showInTile = false;
          }
        }
        // add footnotes to separate array
          for (let z=0; z<parentPhrase.references?.length; z++) {
            let reference = parentPhrase.references[z];
            if (reference.refType == 'FOOTNOTE' && reference.showInTile) {
                reference.showInTile = false;
                parentPhrase.referencesInFootnotesSection.push(reference);
            }
          }

      }        
    }
    this.setNumberOfElementsToShow(results);

      rulesToDelete.forEach(ruleToDelete => {
        if (!ruleToDelete.isImage) {
          
          results = results.filter(phrase => phrase.phraseId != ruleToDelete.id);
        } else {
          
          results = results.filter(phrase => phrase.imageId != ruleToDelete.id);
        }
      })
    return results;

  }
  private setNumberOfElementsToShow(revRules: RevRule[]) {

    revRules.forEach((rule) => {
      rule.showAssociatedTextSection = false;
      rule.showAssociatedImagesSection = false;
      rule.showAssociatedTablesSection = false;
      rule.showReferencesSection = false;
      rule.showAssociatedBySection = false;
      rule.showReferencedBySection = false;
      rule.showFootnotesSection = false;
      rule.showTablesSection = false;
      rule.showImages = false;
      rule.numOfContextCatPhrases = 0;
      rule.numberOfAssociatedBy = 0;
      rule.numberOfReferencedBy = 0;
      rule.numberOfFootnotes = 0;
      rule.numberOfTables = 0;
      rule.numberOfImages = 0;

      if (rule.associatedText) {
        for (let i = 0; i < rule.associatedText.length; i++) {
          if (rule.associatedText[i].showInTile) {
            rule.showAssociatedTextSection = true;
            break;
          }
        }

        rule.showAssociatedTextSection = rule.showAssociatedTextSection || rule.contentChildren.length > 0;

      }
      //images section
      if (rule.associatedImages) {
        for (let i = 0; i < rule.associatedImages.length; i++) {
          if (rule.associatedImages[i].showInTile && rule.associatedImages[i].type != 'TABLE') {
            rule.showAssociatedImagesSection = true;
            rule.numberOfImages++;
          }
        }
      }

      // tables section
      rule.showImages = rule.showAssociatedImagesSection || rule.imageChildren.length > 0 ;
      rule.numberOfImages += rule.imageChildren.length;
      if (rule.associatedImages) {
        for (let associatedImage of rule.associatedImages) {
          if (associatedImage.showInTile && associatedImage.type == 'TABLE') {
            
            rule.showAssociatedTablesSection = true;
            rule.numberOfTables++;

          }
        }
      }
      rule.showTablesSection = rule.showAssociatedTablesSection || ('Tables' in rule.children);
      rule.numberOfTables += rule.children['Tables'] ? rule.children['Tables'].length : 0;
      if (rule.references) {
        for (let i = 0; i < rule.references.length; i++) {
          if (rule.references[i].showInTile) {
            rule.showReferencesSection = true;
            rule.numOfContextCatPhrases++;
          }
        }
      }
      if (rule.associatedBy) {
        for (let i = 0; i < rule.associatedBy.length; i++) {
          if (rule.associatedBy[i].showInTile) {
            rule.showAssociatedBySection = true;
            rule.numberOfAssociatedBy++;
          }
        }
      }
      if (rule.referencedBy) {  
        for (let i = 0; i < rule.referencedBy.length; i++) {
          if (rule.referencedBy[i].showInTile) {
            rule.showReferencedBySection = true;
            rule.numberOfReferencedBy++;
          }
        }
      }
      if (rule.referencesInFootnotesSection.length > 0 || ('Footnotes' in rule.children)) {
          rule.showFootnotesSection = true;
          let tileFootnotesLength = 0;
          if (rule.children['Footnotes'] != null) {
              tileFootnotesLength = rule.children['Footnotes'].length;
          }
          rule.numberOfFootnotes = rule.referencesInFootnotesSection.length + tileFootnotesLength;

      }
    })
  }

  recalculateRuleSections(rule: RevRule): void {
    this.setNumberOfElementsToShow([rule]);
  }

  mapRevRuleIgnoredStatusCodes() {

    let revRuleIgnoredStatusCodeArr = this.ngRedux.getState().revRuleIgnoredStatusCodes;
    let revRuleIgnoredStatusCode = "";

    for (let i=0; i<revRuleIgnoredStatusCodeArr.length; i++) {
      
      revRuleIgnoredStatusCode = revRuleIgnoredStatusCode + revRuleIgnoredStatusCodeArr[i].id + ","  
    }

    return revRuleIgnoredStatusCode.slice(0, -1);
  }
/* 
  private mapRevRuleIgnoredStatusCodes() {

    let revRuleIgnoredStatusCode: string = "";

    const revRuleIgnoredStatusCodeArr = this.ngRedux.getState().revRuleIgnoredStatusCodes;
    for (let i = 0; i < revRuleIgnoredStatusCodeArr.length; i++) {
      const revRuleIgnoredStatusCodeVal = revRuleIgnoredStatusCodeArr[i];

      switch (revRuleIgnoredStatusCodeVal) {
        case 'passedCnt':

          revRuleIgnoredStatusCode = revRuleIgnoredStatusCode + RevStatus.PASSED + ",";
          break;
        case 'failedCnt':

          revRuleIgnoredStatusCode = revRuleIgnoredStatusCode + RevStatus.NOT_PASSED + ",";
          break;
        case 'dismissedCnt':

          revRuleIgnoredStatusCode = revRuleIgnoredStatusCode + RevStatus.DISMISSED + ",";
          break;
        case 'markedForChangesCnt':

          revRuleIgnoredStatusCode = revRuleIgnoredStatusCode + RevStatus.MARKED_FOR_CHANGES + ",";
          break;
        case 'notPassedVarCnt':

          revRuleIgnoredStatusCode = revRuleIgnoredStatusCode + RevStatus.NOT_PASSED_VAR + ",";
          break;
        case 'notPassedSevCnt':

          revRuleIgnoredStatusCode = revRuleIgnoredStatusCode + RevStatus.NOT_PASSED_SEV + ",";
          break;
        case 'newContentCnt':

          revRuleIgnoredStatusCode = revRuleIgnoredStatusCode + RevStatus.NOT_PASSED_SEV + ",";

          break;
        case 'stetCnt':

          revRuleIgnoredStatusCode = revRuleIgnoredStatusCode + RevStatus.STET + ",";

          break;
        case 'newClaimCnt':

          revRuleIgnoredStatusCode = revRuleIgnoredStatusCode + RevStatus.NEW_CLAIM + ",";
          break;
        case 'claimVariantCnt':
          revRuleIgnoredStatusCode = revRuleIgnoredStatusCode + RevStatus.CLAIM_VARIANT + ",";
          break;
      }
    }
    
    revRuleIgnoredStatusCode = revRuleIgnoredStatusCode.slice(0, -1);
    
    return revRuleIgnoredStatusCode;

  }
 */

  mapRevIgnoredRules(revIgnoredRules: string | null): {id: string; key:string}[] {
    if (revIgnoredRules == null || revIgnoredRules == "") {
      return [];
    }
    let mappedRevIgnoredRules: {id: string; key:string}[] = [];
    let ignoredRulesArr: string[] = revIgnoredRules.split(',');

    for (let i=0; i<ignoredRulesArr.length; i++) {
      const curRule = ignoredRulesArr[i];
      switch(curRule) {
        case RevStatus.PASSED: 
          mappedRevIgnoredRules.push({id: RevStatus.PASSED, key:  'passedCnt'})
          break;
        
        case RevStatus.NOT_PASSED:

          mappedRevIgnoredRules.push({id: RevStatus.NOT_PASSED, key:  'failedCnt'})
          break;
        case RevStatus.DISMISSED:

          mappedRevIgnoredRules.push({id: RevStatus.DISMISSED, key:  'dismissedCnt'})
          break;

        case RevStatus.MARKED_FOR_CHANGES:
          
          mappedRevIgnoredRules.push({id: RevStatus.MARKED_FOR_CHANGES, key:  'markedForChangesCnt'})
          break;

        case RevStatus.NOT_PASSED_VAR:

          mappedRevIgnoredRules.push({id: RevStatus.NOT_PASSED_VAR, key:  'notPassedVarCnt'})
          break;

        case RevStatus.NOT_PASSED_SEV:

          mappedRevIgnoredRules.push({id: RevStatus.NOT_PASSED_SEV, key:  'notPassedSevCnt'})
          break;

        case RevStatus.NEW_CONTENT:

          mappedRevIgnoredRules.push({id: RevStatus.NEW_CONTENT, key:  'newContentCnt'})
          break;

        case RevStatus.STET:

          mappedRevIgnoredRules.push({id: RevStatus.STET, key:  'stetCnt'})
          break;

        case RevStatus.NEW_CLAIM:


          mappedRevIgnoredRules.push({id: RevStatus.NEW_CLAIM, key:  'newClaimCnt'})
          break;

        case RevStatus.CLAIM_VARIANT:
      
          mappedRevIgnoredRules.push({id: RevStatus.CLAIM_VARIANT, key:  'claimVariantCnt'})
          break;
      }
    } 

    return mappedRevIgnoredRules;
  }
  revealPhrasesAndImages(runNum: number, docId: number, phrases: number[], contentType: string | null, images: number[]): Observable<any> {
    const url = this.scaiServiceUrl + `reveal`;
    
    let revealPhrasesAndImagesPayload = {
      run_num: runNum,
      doc_id: docId,
      phrases_ids: phrases,
      phrase_content_type: contentType,
      images_ids: images
    }

    return this.http.post(url, revealPhrasesAndImagesPayload, {
      headers: this.authService.getRequestHeaders(),
      withCredentials: false,
      responseType: 'json'
    });
  }

  createClaimBlock(runNum: number, docId: number, pageNum: number, box: BoundingBox): Observable<RevClaimBlockResult> {
    const url = this.scaiServiceUrl + `precheck/claim-block`;

    let claimBlockPayload = {
      run_num: runNum,
      doc_id: docId,
      page_num: pageNum,
      x1: box.x1,
      y1: box.y1,
      x2: box.x2,
      y2: box.y2
    }

    return this.http.post<RevClaimBlockResult>(url, claimBlockPayload, {
      headers: this.authService.getRequestHeaders(),
      withCredentials: false,
      responseType: 'json'
    });
  }

  getRevDocViewInfo(queryParams: Params): Observable<{ env_id: number, doc_id: number }> {
    const url = this.scaiServiceUrl + 'rev-doc-view-info';
    return this.http.get<{ env_id: number, doc_id: number }>(url, {
      headers: this.authService.getRequestHeaders(),
      withCredentials: false,
      responseType: 'json',
      params: queryParams
    });
  }
  copyRevRuleData(target: RevRule, source: RevRule, excludedProperties: string[]) {
        //references 
        //associatedText
        //associatedBy
        // associatedImages

  
       Object.keys(source).forEach((key) => {
        if (!excludedProperties.includes(key)) {
          target[key] = source[key]
        }
      });

  }

  updateRevRule(revRuleToUpdate: RevRule) {
    console.log(revRuleToUpdate);
    let revRules = new Array().concat(this.ngRedux.getState().revDocRules);
    let revRuleIndex = revRules.findIndex((revRule) => revRule.revRuleId == revRuleToUpdate.revRuleId);
    if (revRuleIndex != -1) {
      revRules[revRuleIndex] = revRuleToUpdate;
     let rules = this._extractChildren(revRules);
     console.log("upd rev rule called");
     return rules.find(rule => rule.revRuleId == revRuleToUpdate.revRuleId);
    }

    return revRuleToUpdate;
  }

  mergeAllChildren(revRule: RevRule): RevRule[] {
    
    let mergedChildren = [];
    let children = revRule.children;
    Object.keys(children).forEach((key) => {
        mergedChildren = mergedChildren.concat(children[key]);
    })
    mergedChildren = mergedChildren.concat(revRule.imageChildren);
    mergedChildren = mergedChildren.concat(revRule.contentChildren);
    return mergedChildren;
  }
  setRuleChildren(src: RevRule, target: RevRule): RevRule {
    
      let arr = this.mergeAllChildren(src);
      arr.push(target);
      this._extractChildren(new Array().concat(arr));
      this.removeParentPhraseFromPhrasesArrays(target, src.parentPhraseId);
      
      return target;
  }
  removeParentPhraseFromPhrasesArrays(revRule: RevRule, parentPhraseId: number) {


    let parentReferencedBy = revRule.referencedBy?.find((reference) => reference.phraseId == parentPhraseId)
    if (parentReferencedBy != null) {
      parentReferencedBy.showInTile = false;
    }
    let parentAssociatedBy = revRule.associatedBy?.find((associatedText) => associatedText.phraseId == parentPhraseId)
    if (parentAssociatedBy != null) {
      parentAssociatedBy.showInTile = false;
    }
    this.setNumberOfElementsToShow([revRule]);
  }
}
